// Parses a reporting NumberStr (a decimal string) into a finite number, treating
// nullish/blank/non-numeric input as 0 so aggregate math never produces NaN.
export const parseNumberStr = (value: number | string | null | undefined): number => {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    const n = typeof value === 'string' ? parseFloat(value) : value;
    return Number.isFinite(n) ? n : 0;
};

const roundToString = (num: number, decimals: number): string => {
    const factor = 10 ** decimals;
    return String(Math.round(num * factor) / factor);
};

const largestRemainderUnits = (weights: number[], scale: number, minPositiveUnits = 0): number[] => {
    if (weights.length === 0) return [];
    const positiveCount = weights.filter((weight) => weight > 0).length;
    const total = weights.reduce((sum, weight) => sum + Math.max(weight, 0), 0);
    if (total === 0) return weights.map(() => 0);
    if (positiveCount * minPositiveUnits > scale) {
        throw new Error(`Cannot assign positive rounded values to ${positiveCount} weights at scale ${scale}`);
    }

    const units = weights.map((weight) => (weight > 0 ? minPositiveUnits : 0));
    const remainingUnits = scale - units.reduce((sum, unit) => sum + unit, 0);
    const scaledRemainders = weights.map((weight, i) => {
        if (weight <= 0) return { i, frac: 0 };

        const scaled = (weight * remainingUnits) / total;
        const wholeUnits = Math.floor(scaled);
        units[i] += wholeUnits;
        return { i, frac: scaled - wholeUnits };
    });
    const deficit = scale - units.reduce((sum, unit) => sum + unit, 0);

    scaledRemainders
        .sort((a, b) => b.frac - a.frac)
        .slice(0, deficit)
        .forEach(({ i }) => (units[i] += 1));

    return units;
};

/**
 * Round fractional weights (e.g. [0.0228, 0.7015, ...]) to percent values that
 * sum to exactly 100 at the requested precision. Uses the Largest Remainder
 * (Hamilton's) method so the residual is distributed to the rows with the
 * largest truncated fraction, instead of biasing toward any one row.
 *
 * Input weights need not be normalized; they are treated as parts of their
 * own sum. An all-zero input returns all-zero output.
 */
export const roundedPercents = (weights: number[], decimals = 2): number[] => {
    const scale = 10 ** (decimals + 2); // target integer sum (e.g. decimals=2 -> 10_000 = 100.00%)
    const denom = 10 ** decimals;
    return largestRemainderUnits(weights, scale).map((units) => units / denom);
};

/**
 * Round fractional weights so they sum to exactly 1 at the requested precision.
 * Positive input weights remain positive where the requested precision allows it.
 */
export const roundedFractions = (weights: number[], decimals = 4): number[] => {
    const scale = 10 ** decimals;
    return largestRemainderUnits(weights, scale, 1).map((units) => units / scale);
};

export function fractionalToPercentString(fractional: string | null | undefined): string | undefined;
export function fractionalToPercentString(fractional: string | null | undefined, defaultValue: string): string;
export function fractionalToPercentString(
    fractional: string | null | undefined,
    defaultValue?: string,
): string | undefined {
    if (fractional == null) {
        return defaultValue;
    }
    const fractionalNum = parseFloat(fractional);
    if (Number.isNaN(fractionalNum)) {
        return defaultValue;
    }
    return roundToString(fractionalNum * 100, 2);
}

export function percentToFractionalString(percent: string | null | undefined): string | undefined;
export function percentToFractionalString(percent: string | null | undefined, defaultValue: string): string;
export function percentToFractionalString(
    percent: string | null | undefined,
    defaultValue?: string,
): string | undefined {
    if (percent == null) {
        return defaultValue;
    }
    const percentNum = parseFloat(percent);
    if (Number.isNaN(percentNum)) {
        return defaultValue;
    }
    return roundToString(percentNum / 100, 4);
}
